import { ArrowRight, Terminal, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import HeroVisual from "@/components/HeroVisual";
import TrustMarquee from "@/components/TrustMarquee";
import { scrollToSection } from "@/lib/scrollToSection";
import { useLocale } from "@/i18n/LocaleProvider";
import { subscribeScroll, prefersReducedMotion } from "@/lib/scrollEngine";

const HeroSection = () => {
  const { t } = useTranslation();
  const { localize } = useLocale();
  const rotating = t("hero.rotating", { returnObjects: true }) as string[];
  // Some languages (e.g. German: ", die du dir vorstellst.") open the suffix with
  // punctuation. On mobile the rotating word sits on its own line, so a leading
  // comma would orphan at the start of the next line. Keep that punctuation glued
  // to the rotating word and break only the readable remainder onto the next line.
  const rawSuffix = t("hero.headlineSuffix");
  const leadingPunct = rawSuffix.match(/^\s*([,.;:!?·]+)/)?.[1] ?? "";
  const suffixRest = leadingPunct
    ? rawSuffix.slice(rawSuffix.indexOf(leadingPunct) + leadingPunct.length)
    : rawSuffix;
  const [loaded, setLoaded] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    setLoaded(true);
    const id = setInterval(() => setWordIndex((i) => (i + 1) % rotating.length), 2200);
    return () => clearInterval(id);
  }, [rotating.length]);

  useEffect(() => {
    if (prefersReducedMotion()) return; // keep hero static
    return subscribeScroll(() => setScrollY(window.scrollY));
  }, []);

  // Parallax: the hero content fades out and accelerates upward faster than the
  // page scrolls, so the next section appears to "catch up" and rush in.
  const opacity = Math.max(1 - scrollY / 360, 0);
  const parallaxY = -scrollY * 0.35;

  return (
    <section
      id="home"
      className="relative min-h-[100dvh] flex items-center overflow-hidden bg-background border-b border-border pt-24 pb-32 md:pb-28 md:pt-36"
    >
      {/* Layered tech background */}
      <div className="absolute inset-0 tech-grid opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-b from-background/0 via-background/40 to-background" />
      <div
        className="absolute -top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(186 100% 44% / 0.10) 0%, transparent 60%)" }}
      />

      <div
        className="relative container will-change-transform"
        style={{ opacity, transform: `translate3d(0, ${parallaxY}px, 0)` }}
      >
        <div className="max-w-4xl">
          <div
            className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-border bg-card/60 backdrop-blur-sm mb-5 md:mb-8 transition-[opacity,transform,filter] duration-700 ${loaded ? "opacity-100 translate-y-0 blur-0" : "opacity-0 translate-y-6 blur-sm"}`}
            style={{ transitionDelay: "120ms" }}
          >
            <Terminal size={13} className="text-accent" />
            <span className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground">
              {t("hero.badge")}
            </span>
          </div>

          <h1
            className="text-[1.9rem] sm:text-5xl md:text-6xl font-bold text-foreground leading-[1.04] tracking-tight"
          >
            {/* One single readable sentence — no duplicated screen-reader copy,
                so crawlers and LLMs extract exactly one clean H1. */}
            <span>{t("hero.headlinePrefix")}</span>
            <br />
            <span className="gradient-text inline-block transition-opacity duration-500">
              {rotating[wordIndex]}{leadingPunct}
            </span>
            {/* Rotating word always keeps its own line so the headline never reflows when the word length changes */}
            {suffixRest.trim() && (
              <>
                <br />
                <span className="sm:whitespace-nowrap">{suffixRest}</span>
              </>
            )}



          </h1>

          <p
            className={`mt-4 md:mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed transition-[opacity,transform,filter] duration-700 ${loaded ? "opacity-100 translate-y-0 blur-0" : "opacity-0 translate-y-8 blur-sm"}`}
            style={{ transitionDelay: "260ms" }}
          >
            {t("hero.subtitle")}
          </p>

          <div
            className={`mt-6 md:mt-8 flex flex-col items-stretch gap-2.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3 md:gap-4 transition-[opacity,transform,filter] duration-700 ${loaded ? "opacity-100 translate-y-0 blur-0" : "opacity-0 translate-y-8 blur-sm"}`}
            style={{ transitionDelay: "380ms" }}
          >
            <span className="hover-glow-accent flex-1 sm:flex-none">
              <Link
                to={localize("/contact")}
                className="inline-flex w-full justify-center items-center gap-2 px-4 sm:px-5 md:px-7 py-3.5 btn-clip bg-primary text-primary-foreground btn-label-contrast font-medium whitespace-nowrap transition-transform duration-200 active:scale-[0.97] group"
              >
                {t("common.startBuild")}
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </span>
            <span className="hover-glow-accent flex-1 sm:flex-none">
              <a
                href="#blueprint"
                onClick={(e) => { e.preventDefault(); scrollToSection("blueprint"); }}
                className="inline-flex w-full justify-center items-center gap-2 px-4 sm:px-5 md:px-7 py-3.5 btn-clip btn-clip-outline bg-card/40 text-foreground font-medium whitespace-nowrap transition-transform duration-200 active:scale-[0.97] group"
              >
                <Sparkles size={16} className="text-accent shrink-0" />
                <span className="md:hidden">{t("common.freeBlueprintShort")}</span>
                <span className="hidden md:inline">{t("common.freeBlueprint")}</span>
              </a>
            </span>
          </div>


          {/* Trust strip — plain-language reassurance for non-technical buyers */}
          <div
            className={`mt-6 md:mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 md:gap-y-3 text-sm text-muted-foreground transition-opacity duration-700 ${loaded ? "opacity-100" : "opacity-0"}`}
            style={{ transitionDelay: "500ms" }}
          >
            {(t("hero.trust", { returnObjects: true }) as string[]).slice(0, 3).map((item, i) => (
              <span key={item} className="flex items-center gap-2">
                <span
                  className={`w-1.5 h-1.5 rounded-full bg-accent shrink-0 ${i === 0 ? "animate-pulse" : ""}`}
                />
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="hidden xl:block absolute right-[3%] 2xl:right-[8%] top-1/2 -translate-y-[55%] w-[38vw] max-w-[620px] z-[1]">
        <HeroVisual />
      </div>

      {/* Trust banderole pinned to the lower hero edge — carries the Datora
          track record over to DevCore (mirrors datora.de). */}
      <div className="absolute inset-x-0 bottom-0 z-20">
        <TrustMarquee />
      </div>
    </section>
  );
};

export default HeroSection;
